import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

export default function Admin() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [tab, setTab] = useState("enquiries");
  const [langFilter, setLangFilter] = useState("all");
  const [enquiries, setEnquiries] = useState([]);
  const [players, setPlayers] = useState([]);

  const shownEnquiries = langFilter === "all" ? enquiries : enquiries.filter((q) => (q.lang || "en") === langFilter);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
      api.get("/admin/players").then((r) => setPlayers(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === null) return <div className="min-h-screen bg-obsidian" data-testid="admin-loading" />;

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-obsidian text-[#F5F5F5] font-satoshi flex items-center justify-center px-6" data-testid="admin-denied">
        <div className="text-center max-w-md">
          <h1 className="font-cabinet font-black uppercase text-4xl md:text-5xl mb-4 tracking-tight">{t("admin.deniedH1")}</h1>
          <p className="text-white/50 mb-8">{t("admin.deniedCopy")}</p>
          <Link to="/login" data-testid="admin-login-link" className="inline-block rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase tracking-wide px-8 py-4 hover:bg-white transition-colors">
            {t("admin.goLogin")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-obsidian text-[#F5F5F5] font-satoshi min-h-screen px-6 md:px-12 py-12" data-testid="admin-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-volt transition-colors text-sm uppercase tracking-widest" data-testid="admin-back-link">
            <ArrowLeft size={16} /> {t("admin.back")}
          </Link>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-white/50 hover:text-crimson transition-colors text-sm uppercase tracking-widest">
            <LogOut size={16} /> {t("admin.signout")}
          </button>
        </div>

        <h1 className="font-cabinet font-black uppercase text-4xl md:text-5xl mt-8 mb-10 tracking-tight">
          Admin<span className="text-volt">.</span>
        </h1>

        <div className="flex gap-px bg-white/10 border border-white/10 w-fit mb-10" data-testid="admin-tabs">
          {[
            { id: "enquiries", label: `${t("admin.enquiries")} (${enquiries.length})` },
            { id: "players", label: `${t("admin.players")} (${players.length})` },
          ].map((t2) => (
            <button
              key={t2.id}
              onClick={() => setTab(t2.id)}
              data-testid={`admin-tab-${t2.id}`}
              className={`px-6 py-3 font-cabinet font-bold uppercase tracking-wide text-sm transition-colors duration-300 ${
                tab === t2.id ? "bg-volt text-obsidian" : "bg-obsidian text-white/50 hover:text-white"
              }`}
            >
              {t2.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div data-testid="enquiry-list">
            <div className="flex gap-px bg-white/10 border border-white/10 w-fit mb-6" data-testid="enquiry-lang-filter">
              {[
                { id: "all", label: `${t("admin.filterAll")} (${enquiries.length})` },
                { id: "fr", label: `FR (${enquiries.filter((q) => q.lang === "fr").length})` },
                { id: "en", label: `EN (${enquiries.filter((q) => (q.lang || "en") === "en").length})` },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setLangFilter(f.id)}
                  data-testid={`enquiry-filter-${f.id}`}
                  className={`px-5 py-2.5 font-jetbrains text-xs uppercase tracking-widest transition-colors duration-300 ${
                    langFilter === f.id ? "bg-volt text-obsidian" : "bg-obsidian text-white/50 hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="space-y-4">
            {shownEnquiries.map((q) => (
              <article key={q.id} className="bg-surface border border-white/10 p-6 md:p-8" data-testid={`enquiry-${q.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <h2 className="font-cabinet font-bold text-xl uppercase tracking-tight">{q.name}</h2>
                  <div className="flex items-center gap-3">
                    <span
                      className={`font-jetbrains text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 border ${
                        q.lang === "fr" ? "border-volt/50 text-volt" : "border-white/20 text-white/40"
                      }`}
                      data-testid={`enquiry-lang-${q.id}`}
                    >
                      {q.lang === "fr" ? "FR" : "EN"}
                    </span>
                    <span className="font-jetbrains text-xs text-white/40">{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <p className="text-volt text-sm mb-1">{q.email}</p>
                <p className="font-jetbrains text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                  {[q.position, q.location].filter(Boolean).join(" — ") || t("admin.noDetails")}
                </p>
                <p className="text-white/70 leading-relaxed whitespace-pre-wrap">{q.message}</p>
              </article>
            ))}
            {shownEnquiries.length === 0 && <p className="text-white/50">{t("admin.noEnquiries")}</p>}
            </div>
          </div>
        )}

        {tab === "players" && (
          <div className="space-y-4" data-testid="player-list">
            {players.map((p) => (
              <article key={p.id} className="bg-surface border border-white/10 p-6 md:p-8" data-testid={`player-${p.id}`}>
                {p.profile && p.profile.photo_path && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.profile.photo_path}`}
                    alt={`${p.name} profile`}
                    className="w-20 h-20 object-cover border border-white/10 mb-4"
                    data-testid={`player-photo-${p.id}`}
                  />
                )}
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <h2 className="font-cabinet font-bold text-xl uppercase tracking-tight">{p.name || t("admin.unnamed")}</h2>
                  <span className="font-jetbrains text-xs text-white/40">
                    {t("admin.joined")} {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                  </span>
                </div>
                <p className="text-volt text-sm mb-3">{p.email}</p>
                {p.profile ? (
                  <div className="text-white/70 text-sm space-y-1">
                    <p className="font-jetbrains text-xs uppercase tracking-[0.2em] text-white/40">
                      {[p.profile.position, p.profile.grad_year && `${t("admin.classOf")} ${p.profile.grad_year}`, p.profile.location]
                        .filter(Boolean)
                        .join(" — ") || t("admin.notFilled")}
                    </p>
                    {p.profile.current_club && <p>{t("admin.club")} {p.profile.current_club}</p>}
                    {p.profile.highlight_url && (
                      <a href={p.profile.highlight_url} target="_blank" rel="noopener noreferrer" className="text-volt hover:text-white transition-colors" data-testid={`player-highlight-${p.id}`}>
                        {t("admin.watch")}
                      </a>
                    )}
                    {p.profile.bio && <p className="text-white/50 mt-2">{p.profile.bio}</p>}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm">{t("admin.notFilled")}</p>
                )}
              </article>
            ))}
            {players.length === 0 && <p className="text-white/50">{t("admin.noPlayers")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
